import { useState } from "react";
import { Star } from "lucide-react";
import { Badge, CategoryBadge, DataTable, EmptyState, ErrorState, TrialBanner } from "@/components/DataTable";
import { FilterBar, PageHead, SelectField, TextField } from "@/components/Filters";
import { addToWatchlist, saveSearch, useFilterOptions, useModuleData } from "./hooks";

const INIT = {
  counselling_body: "",
  year: "",
  round: "",
  college: "",
  branch: "",
  quota: "",
  category: "",
};

export default function Allotment() {
  const opts = useFilterOptions();
  const [f, setF] = useState(INIT);
  const [sort, setSort] = useState({ by: "rank", dir: 1 });
  const { rows, total, locked, visible, loading, error } = useModuleData("/allotments", {
    ...f,
    sort_by: sort.by,
    sort_dir: sort.dir,
  });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const onSort = (key) => setSort((s) => ({ by: key, dir: s.by === key ? -s.dir : 1 }));

  const text = (v) => (v == null || v === "" ? "NA" : v);
  const columns = [
    {
      key: "college",
      label: "College",
      minWidth: 340,
      render: (r) => (
        <span className="block max-w-[420px] whitespace-normal" title={r.college}>
          {text(r.college)}
        </span>
      ),
    },
    { key: "state", label: "State", width: 110, render: (r) => <span className="text-slate-600">{text(r.state)}</span> },
    { key: "branch", label: "Branch", minWidth: 170, render: (r) => text(r.branch) },
    { key: "quota", label: "Quota", width: 130, render: (r) => (r.quota ? <Badge tone="blue" width="quota">{r.quota}</Badge> : <span className="text-slate-400">NA</span>) },
    { key: "raw_category", label: "Category", width: 100, render: (r) => <CategoryBadge code={r.raw_category} /> },
    { key: "year", label: "Year", width: 70, muted: true, render: (r) => text(r.year) },
    { key: "round", label: "Round", width: 90, muted: true, render: (r) => text(r.round) },
    { key: "rank", label: "Rank", align: "right", width: 110, highlight: true, render: (r) => (r.rank == null ? "NA" : Number(r.rank).toLocaleString("en-IN")) },
  ];

  return (
    <div className="space-y-4" data-testid="allotment-page">
      <PageHead
        title="Allotment Data"
        subtitle="Published allotment records with raw counselling codes preserved alongside normalized quota."
        right={
          <span className="font-mono-num text-sm text-slate-500" data-testid="allotment-count">
            {total.toLocaleString("en-IN")} records
          </span>
        }
      />
      <FilterBar onReset={() => setF(INIT)} onSave={() => saveSearch("allotment", f)}>
        <SelectField label="Body" testId="filter-body" value={f.counselling_body} onChange={set("counselling_body")} options={opts.counselling_bodies} />
        <SelectField label="Year" testId="filter-year" value={f.year} onChange={set("year")} options={opts.years} />
        <SelectField label="Round" testId="filter-round" value={f.round} onChange={set("round")} options={opts.rounds} />
        <SelectField label="Quota" testId="filter-quota" value={f.quota} onChange={set("quota")} options={opts.quotas} />
        <SelectField label="Category" testId="filter-category" value={f.category} onChange={set("category")} options={opts.category_codes} />
        <TextField label="College" testId="filter-college" value={f.college} onChange={set("college")} placeholder="Search college" />
        <TextField label="Branch" testId="filter-branch" value={f.branch} onChange={set("branch")} placeholder="Search branch" />
      </FilterBar>

      {error && <ErrorState message={error} />}
      {locked && <TrialBanner total={total} visible={visible} />}

      {loading ? (
        <div className="border border-slate-200 bg-white p-10 text-center text-sm text-slate-400">Loading records…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          testId="allotment-empty"
          title="No results found"
          hint="No allotment records match these filters. Try clearing the college or branch text, or pick a different round."
        />
      ) : (
        <DataTable
          testId="allotment-table"
          columns={columns}
          rows={rows}
          sort={sort}
          onSort={onSort}
          rowActions={(r) => (
            <button
              data-testid={`watch-btn-${r.college}-${r.branch}`}
              onClick={() => addToWatchlist(r.college, r.branch)}
              className="flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-[#1A365D]"
            >
              <Star className="h-3.5 w-3.5" /> Watch
            </button>
          )}
        />
      )}
    </div>
  );
}
